import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { Icon } from '../Icon/Icon';

import styles from './Callout.module.scss';

export type CalloutIntent = 'primary' | 'accent' | 'success' | 'warning' | 'error';

export interface CalloutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
    /** Colour role of the border, the background and the icon; the text keeps the text colour. @default 'primary' */
    intent?: CalloutIntent;
    /** The icon before the title, e.g. `Tag` from `lucide-react`. */
    icon?: LucideIcon;
    /** The main line. */
    title: ReactNode;
    /** More text under the title. */
    children?: ReactNode;
    /** Rendered as given under the text: a `<Button isFullWidth>` or a `<Flex>` of buttons. */
    actions?: ReactNode;
}

/**
 * A coloured notice: an invitation, a confirmation, a warning. The same shape for every intent; the colour tells them
 * apart.
 *
 * @example
 * <Callout intent="success" icon={CheckCircleIcon} title={t('discount.applied')} />
 * <Callout
 *     intent="accent"
 *     icon={TagIcon}
 *     title={t('discount.invite')}
 *     actions={<Button isFullWidth color="accent">{t('discount.sign-up')}</Button>}
 * />
 */
export function Callout({
    intent = 'primary',
    icon,
    title,
    children,
    actions,
    className,
    ...props
}: CalloutProps): ReactElement {
    return (
        <div className={classNames(styles.callout, styles[`intent-${intent}`], className)} {...props}>
            <div className={styles.header}>
                {icon && <Icon icon={icon} size="xl" color={intent} />}
                <div className={styles.title}>{title}</div>
            </div>
            {children && <div className={styles.body}>{children}</div>}
            {actions && <div>{actions}</div>}
        </div>
    );
}
